import { CloudUpload, Delete, Description } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import moment from "moment";
import { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import { isCashPayment } from "./constants";
import { buildUploadFilesFormData, buildUploadMetadataPayload } from "./pureLogic";
import * as api from "./billCollection.service";

const isViewerOnly = () => localStorage.getItem("viewer") === "true";

const DocumentUploadTab = ({
  data,
  finalImgArray,
  setFinalImgArray,
  disableFileUpload,
  onSubmitSuccess,
  // Lens gate (only relevant when isLensApplicable is true)
  isLensApplicable = false,
  hasLensDecision = false,
  lensChoice = "",
  lensSaved = false,
  lensLocked = false,
  onLensChoice,
  onReopenLensModal,
}) => {
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Entry-only amounts — always start EMPTY on every open (never pre-filled).
  const [uploadFinalBillAmount, setUploadFinalBillAmount] = useState("");
  const [uploadNetBillAmount, setUploadNetBillAmount] = useState("");
  const [dragover, setDragover] = useState(false);
  const fileInputRef = useRef(null);

  const paymentMode = data?.payment_mode || "";
  const isCash = isCashPayment(paymentMode);
  const appId = data?.appID || "";
  const userName = localStorage.getItem("userName") || "";
  const userId = localStorage.getItem("userId") || "";

  const handleFiles = useCallback(
    async (files) => {
      // Viewer-only sessions cannot write.
      if (isViewerOnly()) {
        toast.warning("You are only allowed to view.");
        return;
      }
      if (!files?.length || disableFileUpload) return;
      setUploading(true);
      try {
        const formData = buildUploadFilesFormData(files, appId);
        const res = await api.uploadFiles(formData);
        if (res?.status === 200 && res?.data) {
          const newDocs = res.data.map((item) => ({
            user: userId,
            userName,
            dateTimeISO: new Date().toISOString(),
            url: item.Url,
            name: item.fileName,
          }));
          setFinalImgArray((prev) => [...prev, ...newDocs]);
          toast.success("File(s) uploaded");
        }
      } catch (err) {
        console.error(err);
        toast.error("File upload failed");
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = null;
      }
    },
    [appId, disableFileUpload, userId, userName, setFinalImgArray]
  );

  const handleDrop = (e) => {
    e.preventDefault();
    setDragover(false);
    handleFiles(e.dataTransfer.files);
  };

  const deleteAttachment = (index) => {
    if (isViewerOnly()) {
      toast.warning("You are only allowed to view.");
      return;
    }
    setFinalImgArray((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    // Viewer-only guard at the handler level (cannot be bypassed by the UI).
    if (isViewerOnly()) {
      toast.warning("You are only allowed to view.");
      return;
    }
    // Enforce the lens gate at the actual submit point (not just the disabled
    // button) so it cannot be bypassed.
    if (lensLocked) {
      toast.error("Please resolve the lens requirement before uploading the bill.");
      return;
    }
    // No-image guard (mirrors Angular: only when upload is not disabled).
    if (!disableFileUpload && !(finalImgArray && finalImgArray.length > 0)) {
      toast.info("No Image Uploaded");
      return;
    }
    if (isCash && !uploadFinalBillAmount) {
      toast.error("Final Bill Amount is required for Cash");
      return;
    }
    if (isCash && !uploadNetBillAmount) {
      toast.error("Net Bill Amount is required for Cash");
      return;
    }
    setSubmitting(true);
    try {
      const body = buildUploadMetadataPayload(data, finalImgArray, {
        finalBillAmount: uploadFinalBillAmount,
        netBillAmount: uploadNetBillAmount,
      });
      const res = await api.uploadFinalBillCentral(body);
      if (res?.status) {
        // After the uploaded-document call succeeds, update the insurance
        // dashboard with the entered Net Bill Amount (Cash only). Fire-and-
        // continue: a failure here must not block the success flow.
        if (isCash && uploadNetBillAmount) {
          try {
            await api.updateInsDashboard({
              netBillAmount: Number(uploadNetBillAmount),
              userId,
              userName,
              appId,
            });
          } catch {
            /* non-blocking */
          }
        }
        toast.success("Documents submitted successfully");
        onSubmitSuccess?.();
      } else {
        toast.error("Something Went Wrong");
      }
    } catch (err) {
      console.error(err);
      toast.error("Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Submit gating (central parity): a document must be uploaded (unless upload is
  // disabled), and for Cash both amounts must be entered.
  const hasDocs = finalImgArray && finalImgArray.length > 0;
  const cashAmountsFilled =
    !isCash ||
    (String(uploadFinalBillAmount).trim() !== "" &&
      String(uploadNetBillAmount).trim() !== "");
  const isSubmitDisabled =
    submitting || lensLocked || (!disableFileUpload && !hasDocs) || !cashAmountsFilled;

  const downloadFile = async (url, name) => {
    try {
      const res = await api.getSignedUrl([url]);
      const signedUrl = res?.result?.[0] || res?.[0] || url;
      const link = document.createElement("a");
      link.href = signedUrl;
      link.download = name || "document";
      link.target = "_blank";
      link.click();
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <div className="cbc-two-col upload-layout">
        {/* Left — Uploaded Documents */}
        <div className="cbc-card">
          <div className="cbc-card-header">
            Uploaded Documents ({finalImgArray.length})
          </div>
          <div className="cbc-card-body">
            {finalImgArray.length === 0 ? (
              <div className="cbc-empty">No documents yet</div>
            ) : (
              finalImgArray.map((doc, i) => (
                <div className="cbc-doc-row" key={doc.url + i}>
                  <div style={{ minWidth: 0 }}>
                    <span
                      className="cbc-doc-name"
                      onClick={() => downloadFile(doc.url, doc.name)}
                    >
                      <Description sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                      Bill {i + 1}
                    </span>
                    <div className="cbc-doc-date">
                      {doc.dateTimeISO
                        ? moment(doc.dateTimeISO).format("DD MMM YYYY, h:mm a")
                        : ""}
                    </div>
                  </div>
                  <button
                    className="cbc-btn outline"
                    style={{ padding: "4px 8px", minHeight: 32 }}
                    onClick={() => deleteAttachment(i)}
                    title="Delete"
                  >
                    <Delete sx={{ fontSize: 16, color: "#dc2626" }} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right — Upload New */}
        <div className="cbc-card">
          <div className="cbc-card-body">
            {/* Patient info strip */}
            <div className="cbc-patient-strip">
              <div>
                <span>Name: </span>
                {data?.name || data?.patient_name || "-"}
              </div>
              <div>
                <span>Hospital: </span>
                {data?.hospital || data?.hospital_name || "-"}
              </div>
              <div>
                <span>Payment: </span>
                {paymentMode || "-"}
              </div>
            </div>

            {/* Lens gate — only when the final bill flags lens as applicable
                AND no decision has been persisted yet. Shown BEFORE the amount
                fields; the form below stays locked until resolved. */}
            {isLensApplicable && !hasLensDecision && (
              <div className="cbc-lens-gate">
                <div className="cbc-lens-note">
                  Lens is required for this hospital
                </div>
                <div className="cbc-lens-options">
                  <label className="cbc-lens-option">
                    <input
                      type="radio"
                      name="lensChoice"
                      checked={lensChoice === "Applicable"}
                      onChange={() => onLensChoice?.("Applicable")}
                    />
                    <span>Applicable</span>
                  </label>
                  <label className="cbc-lens-option">
                    <input
                      type="radio"
                      name="lensChoice"
                      checked={lensChoice === "Not Applicable"}
                      onChange={() => onLensChoice?.("Not Applicable")}
                    />
                    <span>Not Applicable</span>
                  </label>
                </div>
                {lensChoice === "Applicable" && (
                  <div className="cbc-lens-status">
                    {lensSaved ? (
                      <span className="cbc-lens-ok">Lens details saved.</span>
                    ) : (
                      <span className="cbc-lens-warn">
                        Lens details not saved yet.{" "}
                        <button
                          type="button"
                          className="cbc-lens-reopen"
                          onClick={() => onReopenLensModal?.()}
                        >
                          Enter Lens Details
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Everything below is locked until the lens gate is satisfied */}
            <div className={lensLocked ? "cbc-lens-disabled" : ""}>

            {/* Cash fields — start empty every open */}
            {isCash && (
              <div className="cbc-form-grid" style={{ marginBottom: 12 }}>
                <div>
                  <label className="cbc-label">Final Bill Amount *</label>
                  <input
                    type="number"
                    className="cbc-input"
                    value={uploadFinalBillAmount}
                    onChange={(e) => setUploadFinalBillAmount(e.target.value)}
                    placeholder="Enter amount"
                  />
                </div>
                <div>
                  <label className="cbc-label">Net Bill Amount</label>
                  <input
                    type="number"
                    className="cbc-input"
                    value={uploadNetBillAmount}
                    onChange={(e) => setUploadNetBillAmount(e.target.value)}
                    placeholder="Enter amount"
                  />
                </div>
              </div>
            )}

            {/* Dropzone */}
            {!disableFileUpload && (
              <div
                className={`cbc-dropzone ${dragover ? "dragover" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragover(true);
                }}
                onDragLeave={() => setDragover(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <CloudUpload sx={{ fontSize: 28, color: "#9ca3af", mb: 0.5 }} />
                    <div>Drag &amp; drop files here or click to browse</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                      Images, PDF, DOC, DOCX, ODT
                    </div>
                  </>
                )}
              </div>
            )}
            {disableFileUpload && (
              <div className="cbc-empty">
                Insurance Has Approved the Final Bill. Upload disabled.
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.odt"
              style={{ display: "none" }}
              onChange={(e) => handleFiles(e.target.files)}
            />
            </div>
            {/* /lens-locked wrapper */}
          </div>

          {/* Submit — enabled only after a document is uploaded, and (for Cash)
              after BOTH Final Bill Amount and Net Bill Amount are filled. The
              user can enter the amounts on this screen after uploading, then
              Submit moves them to the Document / Collection Actions tab. */}
          <div style={{ padding: "12px 16px", borderTop: "1px solid #f3f4f6", textAlign: "right" }}>
            <button
              className="cbc-btn primary full-mobile"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
            >
              {submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadTab;
